import { useState, useEffect } from 'react'

const API = `${import.meta.env.VITE_API_URL || 'http://localhost:3001'}`

function AdminUsuarios() {
    const [usuarios, setUsuarios] = useState([])
    const [loading, setLoading] = useState(true)
    const [erro, setErro] = useState('')
    const [busca, setBusca] = useState('')
    const [filtro, setFiltro] = useState('todos')
    const [pagina, setPagina] = useState(1)
    const [totalPaginas, setTotalPaginas] = useState(1)
    
    // Modal de Detalhes
    const [selectedUserId, setSelectedUserId] = useState(null)
    const [userDetails, setUserDetails] = useState(null)
    const [loadingDetails, setLoadingDetails] = useState(false)

    const headers = {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('token')}`
    }

    useEffect(() => {
        carregarUsuarios()
    }, [pagina, filtro])

    const carregarUsuarios = async () => {
        setLoading(true)
        setErro('')
        try {
            const params = new URLSearchParams({ page: pagina, limit: 15 })
            if (filtro && filtro !== 'todos') params.append('tipo', filtro)
            if (busca) params.append('search', busca)

            const res = await fetch(`${API}/api/admin/usuarios?${params}`, { headers })
            if (!res.ok) throw new Error('Erro ao carregar usuários')

            const data = await res.json()
            setUsuarios(data.usuarios || [])
            setTotalPaginas(data.totalPaginas || 1)
        } catch (error) {
            console.error('Erro:', error)
            setErro('Não foi possível carregar os usuários.')
        } finally {
            setLoading(false)
        }
    }

    const handleBusca = (e) => {
        e.preventDefault()
        setPagina(1)
        carregarUsuarios()
    }

    const handleAction = async (url, method, body, confirmMsg) => {
        if (confirmMsg && !confirm(confirmMsg)) return
        try {
            const res = await fetch(`${API}${url}`, {
                method,
                headers,
                body: body ? JSON.stringify(body) : undefined
            })
            if (!res.ok) {
                const data = await res.json().catch(() => ({}))
                throw new Error(data.erro || data.message || data.error || 'Erro na operação')
            }
            carregarUsuarios()
        } catch (error) {
            alert('Erro: ' + error.message)
        }
    }

    const handleToggleAdmin = (user) => {
        const novoCargo = user.cargo === 'admin' ? 'user' : 'admin'
        handleAction(
            `/api/admin/usuarios/${user.id}/cargo`,
            'PUT',
            { cargo: novoCargo },
            `Alterar cargo de "${user.nome}" para ${novoCargo}?`
        )
    }

    const handleDarCreditos = (user) => {
        const qtd = prompt(`Quantos créditos deseja dar a "${user.nome}"?`, '1')
        if (!qtd || isNaN(qtd) || Number(qtd) <= 0) return
        handleAction(
            `/api/admin/usuarios/${user.id}/creditos`,
            'PUT',
            { quantidade: Number(qtd) }
        )
    }

    const handleAtivarPro = (user) => {
        const temPro = user.assinatura_ilimitada || user.plano_pro || user.planoPro
        const acao = temPro ? 'Remover' : 'Ativar'
        handleAction(
            `/api/admin/usuarios/${user.id}/plano-pro`,
            'PUT',
            {},
            `Deseja ${acao.toLowerCase()} o plano PRO para "${user.nome}"?`
        )
    }

    const handleToggleMotorista = (user) => {
        handleAction(
            `/api/admin/usuarios/${user.id}/motorista`,
            'PUT',
            {},
            `Ativar/desativar motorista para "${user.nome}"?`
        )
    }

    const handleToggleStatus = (user) => {
        const acao = user.ativo ? 'banir' : 'reativar'
        handleAction(
            `/api/admin/usuarios/${user.id}/status`,
            'PUT',
            { ativo: !user.ativo },
            `Deseja ${acao} "${user.nome}"?`
        )
    }

    const handleExcluirUsuario = (user) => {
        handleAction(
            `/api/admin/usuarios/${user.id}`,
            'DELETE',
            null,
            `⚠️ ATENÇÃO EXTREMA!
Deseja mesmo EXCLUIR DEFINITIVAMENTE o usuário "${user.nome}" (${user.email}) do banco de dados?

Esta ação irá deletar de forma permanente:
- Todos os seus anúncios e respectivas fotos/mídias;
- Todas as suas mensagens enviadas e recebidas no chat;
- Todas as suas candidaturas e propostas de frete;
- Sua assinatura ativa ou saldo de créditos.

Esta ação NÃO PODE ser desfeita.`
        )
    }

    const handleExportar = async () => {
        try {
            const res = await fetch(`${API}/api/admin/usuarios/exportar`, { headers })
            if (!res.ok) throw new Error('Erro ao exportar')
            const blob = await res.blob()
            const url = window.URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            a.download = `usuarios_${new Date().toISOString().slice(0, 10)}.csv`
            document.body.appendChild(a)
            a.click()
            a.remove()
            window.URL.revokeObjectURL(url)
        } catch (error) {
            alert('Erro ao exportar: ' + error.message)
        }
    }

    const formatDate = (date) => date ? new Date(date).toLocaleDateString('pt-BR') : '—'

    const handleViewDetails = async (id) => {
        setLoadingDetails(true)
        setSelectedUserId(id)
        try {
            const res = await fetch(`${API}/api/admin/usuarios/${id}`, { headers })
            const data = await res.json()
            if (!res.ok) throw new Error(data.message || 'Erro ao carregar')
            setUserDetails(data)
        } catch (err) {
            alert('Erro ao carregar detalhes do usuário')
        } finally {
            setLoadingDetails(false)
        }
    }

    const closeDetails = () => {
        setSelectedUserId(null)
        setUserDetails(null)
    }

    const filtros = [
        { key: 'todos', label: 'Todos' },
        { key: 'comprador', label: 'Compradores' },
        { key: 'vendedor', label: 'Vendedores' },
        { key: 'motorista', label: 'Motoristas' },
        { key: 'admin', label: 'Admins' },
    ]

    return (
        <div>
            <div className="admin-section-header" style={{ marginBottom: '1.5rem' }}>
                <h3>👥 Gestão de Usuários</h3>
                <button className="admin-btn-export" onClick={handleExportar}>
                    📥 Exportar CSV
                </button>
            </div>

            {erro && <div className="admin-error">{erro}</div>}

            {/* Toolbar */}
            <div className="admin-toolbar">
                <form onSubmit={handleBusca} style={{ flex: 1, display: 'flex', minWidth: 220 }}>
                    <input
                        type="text"
                        className="admin-search-input"
                        placeholder="Buscar por nome ou email..."
                        value={busca}
                        onChange={(e) => setBusca(e.target.value)}
                        style={{ flex: 1 }}
                    />
                </form>
                <div className="admin-filter-tabs">
                    {filtros.map(f => (
                        <button
                            key={f.key}
                            className={`admin-filter-tab ${filtro === f.key ? 'active' : ''}`}
                            onClick={() => { setFiltro(f.key); setPagina(1) }}
                        >
                            {f.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Table */}
            {loading ? (
                <div className="admin-loading">
                    <div className="spinner"></div>
                    <span>Carregando usuários...</span>
                </div>
            ) : (
                <div className="admin-section">
                    <div className="admin-table-wrapper">
                        <table className="admin-table">
                            <thead>
                                <tr>
                                    <th>Nome</th>
                                    <th>Email</th>
                                    <th>Tipo</th>
                                    <th>Ações</th>
                                </tr>
                            </thead>
                            <tbody>
                                {usuarios.length > 0 ? (
                                    usuarios.map(user => {
                                        const isPro = (user.assinatura_ilimitada || user.plano_pro || user.planoPro) && user.expiracao_assinatura && new Date(user.expiracao_assinatura) > new Date();
                                        const hasMotorista = user.tipo_ativo === 'motorista' || 
                                            (user.expiracao_motorista && new Date(user.expiracao_motorista) > new Date()) ||
                                            (user.tipos_habilitados && (
                                                typeof user.tipos_habilitados === 'string' 
                                                    ? user.tipos_habilitados.includes('motorista') 
                                                    : Array.isArray(user.tipos_habilitados) && user.tipos_habilitados.includes('motorista')
                                            ));
                                        
                                        const badges = Array.from(new Set([
                                            user.tipo || 'comprador',
                                            ...(isPro ? ['pro'] : []),
                                            ...(hasMotorista ? ['motorista'] : []),
                                            ...(user.vendas_realizadas > 0 ? ['vendedor'] : []),
                                            ...(user.cargo === 'admin' ? ['admin'] : [])
                                        ].map(t => t.toUpperCase())));

                                        return (
                                            <tr key={user.id} className="admin-clickable-row" onClick={() => handleViewDetails(user.id)}>
                                                <td data-label="Nome" style={{ color: '#f8fafc', fontWeight: 500 }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                        {user.foto_perfil ? (
                                                            <img 
                                                                src={user.foto_perfil.startsWith('http') ? user.foto_perfil : `${API}/uploads/profiles/${user.foto_perfil}`} 
                                                                alt={user.nome} 
                                                                className="user-avatar"
                                                                style={{ width: '32px', height: '32px', borderRadius: '50%', objectFit: 'cover', border: '1px solid rgba(255,255,255,0.1)', flexShrink: 0 }}
                                                            />
                                                        ) : (
                                                            <div style={{ width: '32px', height: '32px', borderRadius: '50%', backgroundColor: '#1e293b', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1rem', border: '1px solid rgba(255,255,255,0.08)', flexShrink: 0, color: '#94a3b8' }}>
                                                                👤
                                                            </div>
                                                        )}
                                                        <span>{user.nome}</span>
                                                    </div>
                                                </td>
                                                <td data-label="Email">{user.email}</td>
                                                <td data-label="Tipo">
                                                    <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                                                        {badges.map((t, idx) => {
                                                            let badgeClass = 'admin-badge-ativo';
                                                            if (t === 'PRO') badgeClass = 'admin-badge-pro';
                                                            else if (t === 'ADMIN') badgeClass = 'admin-badge-admin';
                                                            else if (t === 'MOTORISTA') badgeClass = 'admin-badge-motorista';
                                                            else if (t === 'VENDEDOR') badgeClass = 'admin-badge-vendido';
                                                            
                                                            return (
                                                                <span key={idx} className={`admin-badge ${badgeClass}`}>
                                                                    {t}
                                                                </span>
                                                            );
                                                        })}
                                                    </div>
                                                </td>
                                                <td data-label="Ações">
                                                    <div className="admin-actions" onClick={e => e.stopPropagation()}>
                                                        <button className="admin-btn-icon" onClick={() => handleViewDetails(user.id)} title="Ver Detalhes completo">
                                                            👁️
                                                        </button>
                                                        <button 
                                                            className="admin-action-btn primary"
                                                            onClick={() => handleToggleAdmin(user)}
                                                            title="Toggle Admin"
                                                        >
                                                            🛡️
                                                        </button>
                                                        <button
                                                            className="admin-action-btn info"
                                                            onClick={() => handleDarCreditos(user)}
                                                            title="Dar Créditos"
                                                        >
                                                            💰
                                                        </button>
                                                        <button
                                                            className="admin-action-btn success"
                                                            onClick={() => handleAtivarPro(user)}
                                                            title={isPro ? "Remover PRO" : "Ativar PRO"}
                                                        >
                                                            ⭐
                                                        </button>
                                                        <button
                                                            className="admin-action-btn info"
                                                            onClick={() => handleToggleMotorista(user)}
                                                            title="Toggle Motorista"
                                                        >
                                                            🚚
                                                        </button>
                                                        <button
                                                            className={`admin-action-btn ${user.ativo ? 'danger' : 'success'}`}
                                                            onClick={() => handleToggleStatus(user)}
                                                            title={user.ativo ? 'Banir' : 'Reativar'}
                                                        >
                                                            {user.ativo ? '🚫' : '✅'}
                                                        </button>
                                                        <button
                                                            className="admin-action-btn danger"
                                                            onClick={() => handleExcluirUsuario(user)}
                                                            title="Excluir Definitivamente"
                                                            style={{ marginLeft: '4px' }}
                                                        >
                                                            🗑️
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })
                                ) : (
                                    <tr>
                                        <td colSpan="4" style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>
                                            Nenhum usuário encontrado.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    <div className="admin-pagination">
                        <button disabled={pagina <= 1} onClick={() => setPagina(p => p - 1)}>
                            ← Anterior
                        </button>
                        <span className="page-info">Página {pagina} de {totalPaginas}</span>
                        <button disabled={pagina >= totalPaginas} onClick={() => setPagina(p => p + 1)}>
                            Próxima →
                        </button>
                    </div>
                </div>
            )}

            {selectedUserId && (
                <div className="admin-modal-overlay" onClick={closeDetails} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.7)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div className="admin-modal-content" onClick={e => e.stopPropagation()} style={{ backgroundColor: '#0f172a', padding: '20px', borderRadius: '12px', maxWidth: '700px', width: '90%', maxHeight: '90vh', overflowY: 'auto', border: '1px solid #334155' }}>
                        <div className="admin-modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', borderBottom: '1px solid #334155', paddingBottom: '15px' }}>
                            <h3 style={{ margin: 0, color: 'white', fontSize: '1.25rem' }}>Detalhes do Usuário</h3>
                            <button onClick={closeDetails} style={{ background: 'none', border: 'none', color: '#94a3b8', fontSize: '1.5rem', cursor: 'pointer' }}>&times;</button>
                        </div>
                        
                        {loadingDetails ? (
                            <div style={{ textAlign: 'center', padding: '40px' }}><div className="loading-spinner"></div> Carregando...</div>
                        ) : userDetails ? (
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', color: '#cbd5e1' }}>
                                <div style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '10px' }}>
                                    {userDetails.foto_perfil ? (
                                        <img src={userDetails.foto_perfil.startsWith('http') ? userDetails.foto_perfil : `${API}/uploads/profiles/${userDetails.foto_perfil}`} alt={userDetails.nome} style={{ width: '80px', height: '80px', borderRadius: '50%', objectFit: 'cover' }} />
                                    ) : (
                                        <div style={{ width: '80px', height: '80px', borderRadius: '50%', backgroundColor: '#1e293b', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '2rem' }}>
                                            👤
                                        </div>
                                    )}
                                    <div>
                                        <h2 style={{ margin: '0 0 5px 0', color: 'white' }}>{userDetails.nome}</h2>
                                        <div style={{ color: '#94a3b8' }}>{userDetails.email}</div>
                                        <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                                            {userDetails.ativo ? <span className="admin-badge" style={{ backgroundColor: '#10b981', color: '#0f172a' }}>ATIVO</span> : <span className="admin-badge" style={{ backgroundColor: '#ef4444', color: 'white' }}>INATIVO</span>}
                                            <span className="admin-badge admin-badge-pro">{userDetails.cargo === 'admin' ? 'ADMIN' : 'USUÁRIO'}</span>
                                        </div>
                                    </div>
                                </div>

                                <div className="detail-group" style={{ backgroundColor: '#1e293b', padding: '15px', borderRadius: '8px' }}>
                                    <div style={{ color: '#94a3b8', fontSize: '0.8rem', marginBottom: '5px' }}>Telefone / Celular</div>
                                    <div>{userDetails.telefone || '-'}</div>
                                </div>

                                <div className="detail-group" style={{ backgroundColor: '#1e293b', padding: '15px', borderRadius: '8px' }}>
                                    <div style={{ color: '#94a3b8', fontSize: '0.8rem', marginBottom: '5px' }}>WhatsApp</div>
                                    <div>{userDetails.whatsapp || '-'}</div>
                                </div>

                                <div className="detail-group" style={{ backgroundColor: '#1e293b', padding: '15px', borderRadius: '8px' }}>
                                    <div style={{ color: '#94a3b8', fontSize: '0.8rem', marginBottom: '5px' }}>CPF/CNPJ</div>
                                    <div>{userDetails.cpf_cnpj || '-'}</div>
                                </div>

                                <div className="detail-group" style={{ backgroundColor: '#1e293b', padding: '15px', borderRadius: '8px' }}>
                                    <div style={{ color: '#94a3b8', fontSize: '0.8rem', marginBottom: '5px' }}>Data de Cadastro</div>
                                    <div>{new Date(userDetails.criado_em).toLocaleString('pt-BR')}</div>
                                </div>

                                <div className="detail-group" style={{ backgroundColor: '#1e293b', padding: '15px', borderRadius: '8px', gridColumn: '1 / -1' }}>
                                    <div style={{ color: '#94a3b8', fontSize: '0.8rem', marginBottom: '5px' }}>Endereço Pessoal</div>
                                    <div>{userDetails.endereco ? `${userDetails.endereco}, ` : ''}{userDetails.cidade || '-'} - {userDetails.estado || '-'}</div>
                                </div>
                                
                                {userDetails.nome_fazenda && (
                                    <div className="detail-group" style={{ backgroundColor: '#1e293b', padding: '15px', borderRadius: '8px', gridColumn: '1 / -1' }}>
                                        <div style={{ color: '#94a3b8', fontSize: '0.8rem', marginBottom: '5px' }}>Dados da Fazenda</div>
                                        <div><strong>Nome:</strong> {userDetails.nome_fazenda}</div>
                                        <div><strong>Local:</strong> {userDetails.cidade_fazenda || '-'} - {userDetails.estado_fazenda || '-'}</div>
                                    </div>
                                )}

                                <div className="detail-group" style={{ backgroundColor: '#1e293b', padding: '15px', borderRadius: '8px', gridColumn: '1 / -1' }}>
                                    <div style={{ color: '#94a3b8', fontSize: '0.8rem', marginBottom: '5px' }}>Planos e Benefícios</div>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                        <div><strong>Créditos de Anúncio:</strong> {userDetails.creditos_anuncio || 0}</div>

                                        <div>
                                            <strong>Plano PRO:</strong> {userDetails.assinatura_ilimitada && userDetails.expiracao_assinatura && new Date(userDetails.expiracao_assinatura) > new Date() ? (
                                                <span>
                                                    Sim (Expira em: {new Date(userDetails.expiracao_assinatura).toLocaleDateString()} 
                                                    <span style={{ color: '#10b981', marginLeft: '5px' }}>
                                                        — Faltam {Math.max(0, Math.ceil((new Date(userDetails.expiracao_assinatura) - new Date()) / (1000 * 60 * 60 * 24)))} dias
                                                    </span>)
                                                </span>
                                            ) : userDetails.assinatura_ilimitada && userDetails.expiracao_assinatura ? (
                                                <span style={{ color: '#ef4444' }}>
                                                    Não (Expirou em {new Date(userDetails.expiracao_assinatura).toLocaleDateString()})
                                                </span>
                                            ) : 'Não'}
                                        </div>
                                        <div>
                                            <strong>Plano Motorista:</strong> {userDetails.motorista_verificado && userDetails.expiracao_motorista && new Date(userDetails.expiracao_motorista) > new Date() ? (
                                                <span>
                                                    Sim (Expira em: {new Date(userDetails.expiracao_motorista).toLocaleDateString()}
                                                    <span style={{ color: '#10b981', marginLeft: '5px' }}>
                                                        — Faltam {Math.max(0, Math.ceil((new Date(userDetails.expiracao_motorista) - new Date()) / (1000 * 60 * 60 * 24)))} dias
                                                    </span>)
                                                </span>
                                            ) : userDetails.motorista_verificado && userDetails.expiracao_motorista ? (
                                                <span style={{ color: '#ef4444' }}>
                                                    Não (Expirou em {new Date(userDetails.expiracao_motorista).toLocaleDateString()})
                                                </span>
                                            ) : 'Não'}
                                        </div>
                                        <div><strong>Vendas Realizadas:</strong> {userDetails.vendas_realizadas || 0}</div>
                                    </div>
                                </div>

                                {(userDetails.tipo_ativo === 'motorista' || userDetails.tipo_veiculo) && (
                                    <div className="detail-group" style={{ backgroundColor: '#1e293b', padding: '15px', borderRadius: '8px', gridColumn: '1 / -1' }}>
                                        <div style={{ color: '#94a3b8', fontSize: '0.8rem', marginBottom: '10px' }}>Dados do Veículo</div>
                                        <div style={{ display: 'flex', gap: '20px', alignItems: 'flex-start' }}>
                                            {userDetails.foto_veiculo && (
                                                <img 
                                                    src={userDetails.foto_veiculo.startsWith('http') ? userDetails.foto_veiculo : (userDetails.foto_veiculo.replace(/^\//, '').startsWith('uploads/') ? `${API}/${userDetails.foto_veiculo.replace(/^\//, '')}` : `${API}/uploads/${userDetails.foto_veiculo.replace(/^\//, '')}`)} 
                                                    alt="Veículo" 
                                                    style={{ width: '120px', height: '120px', objectFit: 'cover', borderRadius: '8px', border: '1px solid #334155' }} 
                                                />
                                            )}
                                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', flex: 1 }}>
                                                <div><strong>Tipo:</strong> {userDetails.tipo_veiculo || '-'}</div>
                                                <div><strong>Modelo:</strong> {userDetails.modelo_veiculo || '-'}</div>
                                                <div><strong>Placa:</strong> {userDetails.placa_veiculo || '-'}</div>
                                                <div><strong>Capacidade (Peso):</strong> {userDetails.capacidade_peso ? `${userDetails.capacidade_peso} kg` : '-'}</div>
                                                <div><strong>Capacidade (Gado):</strong> {userDetails.capacidade_gado ? `${userDetails.capacidade_gado} cabeças` : '-'}</div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                <div className="detail-group" style={{ backgroundColor: '#1e293b', padding: '15px', borderRadius: '8px', gridColumn: '1 / -1' }}>
                                    <div style={{ color: '#94a3b8', fontSize: '0.8rem', marginBottom: '10px', fontWeight: 600 }}>
                                        📢 Anúncios Publicados ({userDetails.total_anuncios || 0})
                                    </div>
                                    {userDetails.anuncios && userDetails.anuncios.length > 0 ? (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '10px', maxHeight: '300px', overflowY: 'auto', paddingRight: '5px' }}>
                                            {userDetails.anuncios.map((anuncio) => (
                                                <div key={anuncio.id} style={{ display: 'flex', gap: '12px', alignItems: 'center', backgroundColor: '#0f172a', padding: '10px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.05)' }}>
                                                    <img 
                                                        src={anuncio.imagem ? (anuncio.imagem.startsWith('http') ? anuncio.imagem : `${API}/${anuncio.imagem.replace(/^\//, '')}`) : 'https://via.placeholder.com/150x100?text=Sem+Foto'} 
                                                        alt={anuncio.titulo} 
                                                        style={{ width: '60px', height: '45px', objectFit: 'cover', borderRadius: '4px', border: '1px solid rgba(255,255,255,0.1)', flexShrink: 0 }}
                                                    />
                                                    <div style={{ flex: 1, minWidth: 0 }}>
                                                        <div style={{ color: 'white', fontWeight: 500, fontSize: '0.9rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                            {anuncio.titulo}
                                                        </div>
                                                        <div style={{ fontSize: '0.75rem', color: '#94a3b8', display: 'flex', gap: '8px', marginTop: '2px' }}>
                                                            <span style={{ backgroundColor: 'rgba(255, 255, 255, 0.05)', padding: '1px 5px', borderRadius: '3px' }}>{anuncio.categoria}</span>
                                                            <span>•</span>
                                                            <span>{anuncio.cidade} - {anuncio.estado}</span>
                                                        </div>
                                                    </div>
                                                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                                                        <div style={{ color: '#10b981', fontWeight: 600, fontSize: '0.9rem' }}>
                                                            {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(anuncio.preco)}
                                                        </div>
                                                        <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end', alignItems: 'center', marginTop: '2px' }}>
                                                            <span style={{ fontSize: '0.7rem', color: '#64748b' }}>👁️ {anuncio.visualizacoes || 0}</span>
                                                            <span className="admin-badge" style={{ fontSize: '0.65rem', padding: '1px 6px', backgroundColor: anuncio.ativo ? 'rgba(16, 185, 129, 0.15)' : 'rgba(239, 68, 68, 0.15)', color: anuncio.ativo ? '#10b981' : '#ef4444', border: 'none', margin: 0 }}>
                                                                {anuncio.ativo ? 'ATIVO' : 'INATIVO'}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div style={{ padding: '20px', textAlign: 'center', color: '#64748b', fontSize: '0.85rem', backgroundColor: '#0f172a', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.03)' }}>
                                            Este usuário ainda não publicou nenhum anúncio.
                                        </div>
                                    )}
                                </div>
                            </div>
                        ) : null}
                    </div>
                </div>
            )}
        </div>
    )
}

export default AdminUsuarios
